import * as Logger from '../../common/tools/logger';
import { PermissionData, PermissionModel } from '../../interfaces';
import { PpsPermissionData } from '../interfaces';
import { PpsConfig } from './pps.service';

const moduleName = 'PermissionModelUtil';

/**
 * @public
 * @function updatePermissionModel
 * @description Function to set the permission data for a single `permissionType`.
 * @param {PermissionModel} permissionModel The model to update
 * @param {PermissionData} permissionData The permission data, which needs to be set.
 * @return {PermissionModel}
 */
export function updatePermissionModel(permissionModel: PermissionModel, permissionData: PermissionData): PermissionModel {

  // No permission model
  if (!permissionModel) {
    Logger.error(moduleName, `updatePermissionModel`, `No permission model to update.`);
    return null;
  }

  // No permission data
  if (!permissionData) {
    Logger.error(moduleName, `updatePermissionModel`, `Got no permission data to save.`);
    return null;
  }

  try {
    // Get local stored permission data.
    const localPermissionData = permissionModel.data;

    // No `localPermissionData` stored.
    if (localPermissionData.length === 0) {
      localPermissionData.push(permissionData);

      Logger.info(moduleName, `updatePermissionModel`, `Successfully set the first permission data entry for permissionType '${permissionData.usecase}'.`, permissionData);
    } else {

      // Reflects, if a permission is locally available.
      let permissionAvailable = false;

      // Loop `localPermissionData`.
      for (let i = 0; i < localPermissionData.length; i++) {

        // If the `permissionData`'s `permissionType` matches the local stored one ...
        if (localPermissionData[i].usecase === permissionData.usecase) {

          // ... overwrite it locally. (@ToDo: Maybe some comparison f.e. with timestamps here?)
          localPermissionData[i] = permissionData;

          // Update value.
          permissionAvailable = true;

          Logger.info(moduleName, `updatePermissionModel`, `Successfully updated permission model entry for permissionType '${permissionData.usecase}'.`, permissionData);
        }
      }

      // After looping, no match, new entry.
      if (!permissionAvailable) {

        // Create new entry.
        localPermissionData.push(permissionData);

        Logger.info(moduleName, `updatePermissionModel`, `Successfully created new permission model entry for permissionType '${permissionData.usecase}'.`, permissionData);
      }
    }

    return {
      identity: null,
      data: localPermissionData,
      tcfVersion: 1,
      tcString: permissionModel.tcString // TODO: update tcString
    };

  } catch (error) {
    Logger.error(moduleName, `updatePermissionData`, error);
    return {
      ...permissionModel
    };
  }
}

/**
 * Returns a clone of the given permission model
 * @param model PermissionModel to clone
 */
export function clonePermissionModel(model: PermissionModel): PermissionModel {
  return {
    identity: model.identity,
    tcString: model.tcString,
    tcfVersion: model.tcfVersion,
    data: model.data.map((perm) => {
      const permClone = {
        usecase: perm.usecase,
        status: perm.status
      };
      for (const prop in perm) {
        /* istanbul ignore else*/
        if (Object.prototype.hasOwnProperty.call(perm, prop)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (permClone as any)[prop] = (perm as any)[prop];
        }
      }
      return permClone;
    })
  };
}

/**
 * Merge a list of PermissionData objects (source) into a PermissionModel (target). If an entry with the same usecase
 * name exists in the target, its status and timestamp will be overwritten with the values from the source
 * @param target PermissionModel to be updated
 * @param source List of PermissionData
 */
export function mergePermissionModels(target: PermissionModel, source: PermissionData[]): PermissionModel {
  const newModel = clonePermissionModel(target);

  source.forEach((remoteData) => {
    const index = newModel.data.findIndex((localData) => localData.usecase === remoteData.usecase);

    if (index !== -1) {
      // apply the values from the Permission Center
      newModel.data[index].timestamp = remoteData.timestamp;
      newModel.data[index].status = remoteData.status;
    } else {
      // add the remote data to the local model
      newModel.data.push(remoteData);
    }
  });

  return newModel;
}

/**
 * Compares two permission models and determines if they are equivalent
 * @param modelA
 * @param modelB
 */
export function diffPermissionData(modelA: PermissionData[], modelB: PermissionData[]): boolean {
  if (modelA.length !== modelB.length) {
    return true;
  }

  function compareData(aModel: PermissionData[], bModel: PermissionData[]): boolean {
    let diff = false;

    aModel.forEach((aData) => {
      const match = bModel.find((bData) => aData.usecase === bData.usecase && aData.status === bData.status);
      if (!match) {
        diff = true;
      }
    });

    return diff;
  }

  return compareData(modelA, modelB) || compareData(modelB, modelA);
}

/**
 * Return a list of permission with missing/falsy timestamp property values
 * @param data permissions to check
 */
export function getPermissionsWithoutTimestamps(data: PermissionData[]): PermissionData[] {
  return data.filter((permission) => !permission.timestamp);
}

/**
 * Converts a permission object from PPS to PPP format
 * @param ppsData
 */
export function ppsToPpp(ppsData: PpsPermissionData): PermissionData {
  return {
    usecase: ppsData.permission_name,
    status: ppsData.status,
    timestamp: ppsData.timestamp
  }
}

/**
 * Converts a permission object from PPP to PPS format
 * @param pppData permission data in PPP format
 * @param settings additional information required by PPS
 */
export function pppToPps(pppData: PermissionData, settings: PpsConfig): PpsPermissionData {
  /* eslint-disable @typescript-eslint/camelcase */
  return {
    permission_name: pppData.usecase,
    status: pppData.status,
    origin_ip: '0.0.0.0',
    touchpoint_name: settings.touchpointName,
    touchpoint_reference: settings.touchpointReference,
    touchpoint_visualization: pppData.touchpointVisualization,
    permission_attributes: [{
      name: 'x-ui-app',
      value: settings.xUIApp
    }]
  };
  /* eslint-enable @typescript-eslint/camelcase */
}
